import React from 'react';
import { Box, Paper, styled, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import AppDetailsRight from '../../components/Dashboard/Application/AppDetailsRight';
import AppDetailsLeft from '../../components/Dashboard/Application/AppDetailsLeft';
import AppMediaPromotion from '../../components/Dashboard/Application/AppMediaPromotion';
import { useLocation, useNavigate } from 'react-router-dom';
import useApplicationDetails from '../../hooks/useApplication';
import EditAppButton from '../../components/CommanButton/EditAppButton';
import Breadcrumb from '../../components/Navigation/Breadcrumb';
import editIcon from '/src/assets/images/button/edit-square.svg?url';
import ApplicationReport from 'components/Dashboard/Application/ApplicationReport';
import { SPACING, RADIUS } from '../../theme/theme';
import { PageLoader } from '../../components/Feedback/Loaders';

// Shared card shell: same background, radius and clipping for all three
// columns. `overflow: hidden` keeps square-cornered children (which paint
// their own white background) from covering the rounded corners.
const cardSx = {
  backgroundColor: 'white',
  borderRadius: `${RADIUS.md}px`,
  overflow: 'hidden',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
} as const;

const PageContainer = styled(Box)({
  width: '100%',
  // Sits inside Layout's <main> (a flex column): grow to fill it rather than
  // asking for a full 100vh, which would overflow by the header/footer height.
  flex: 1,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: `${SPACING.md}px`,
  backgroundColor: 'transparent',
});

const ContentWrapper = styled(Paper)(({ theme }) => ({
  width: '100%',
  maxWidth: '1100px',
  // Transparent so the grey page background separates the white cards
  // (Paper defaults to white, which merged them into one block).
  backgroundColor: 'transparent',
  direction: 'rtl',
  boxShadow: 'none',
  padding: `${SPACING.md}px`,
  [theme.breakpoints.down('sm')]: {
    padding: `${SPACING.sm}px`,
  },
}));


const Application: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { appId, publisherId } = location.state || {};
  const { data, isLoading, isError, error } = useApplicationDetails(
    publisherId,
    appId,
  );

  if (isLoading) {
    return (
      <PageContainer>
        <ContentWrapper>
          <PageLoader label="در حال بارگذاری اطلاعات اپلیکیشن" />
        </ContentWrapper>
      </PageContainer>
    );
  }

  if (isError) {
    return (
      <PageContainer>
        <ContentWrapper>
          <Typography variant="body1">
            Error:{' '}
            {error instanceof Error
              ? error.message
              : 'اپلیکیشنی دراین هویت یافت نشد'}
          </Typography>
        </ContentWrapper>
      </PageContainer>
    );
  }

  const handleEdit = () => {
    navigate('/dashboard/editapp', { state: { appId, publisherId } });
  };

  if (!data || data?.application?.length === 0) {
    return (
      <PageContainer>
        <ContentWrapper>
          <Typography variant="body1">No applications found.</Typography>
        </ContentWrapper>
      </PageContainer>
    );
  }

  const { application } = data || {};
  const {
    name_fa,
    name_en,
    description_fa,
    description_en,
    size_text,
    rate_count,
    logo,
    screenshots,
    versions,
    verersion_text,
    minimum_ios_version,
    last_updated_at_text,
    download_count_text,
    active,
    statuses,
  } = application || {};

  // Live shots only. This page answers "how does my listing look on سیبچه",
  // and the show endpoint also carries images still awaiting review — the edit
  // screen is where those belong, labelled as pending. Showing them here would
  // claim they are already on the store.
  const liveScreenshots = (screenshots ?? []).filter(
    (shot: { is_pending?: boolean }) => !shot.is_pending,
  );

  return (
    <PageContainer>
      <ContentWrapper>
        <Breadcrumb
          items={[
            { label: 'داشبورد', to: '/dashboard' },
            {
              label: 'اپلیکیشن‌ها',
              to: publisherId
                ? `/applications?publisherId=${publisherId}`
                : '/applications',
            },
            { label: name_fa || name_en || 'اپلیکیشن' },
          ]}
        />
        {/* ردیف اول با سه ستون */}
        <Grid2 container spacing={SPACING.md} sx={{ marginBottom: `${SPACING.md}px` }}>
          {/* ستون اول */}
          <Grid2 component="div" xs={12} md={4} lg={4}>
            <Box sx={{ ...cardSx, width: '100%' }}>
              <AppDetailsRight
                iconSrc={logo}
                appName={`${name_fa} | ${name_en}`}
                appVersion={verersion_text}
                lastUpdateDate={last_updated_at_text}
                lastUpdateTime="22:48"
                appSize={size_text}
                compatibility={minimum_ios_version}
                ageRestriction="ندارد"
                price="رایگان"
                rate_count={rate_count}
                download_count_text={download_count_text}
                versions={versions}
                publisherId={publisherId}
                appId={appId}
                // The api sends 1/0, the component wants a boolean.
                active={Number(active) === 1}
                // Both publisher controls only make sense once sibche has
                // approved and published the listing.
                canManagePublish={
                  statuses?.publish === 'PUBLISHED' &&
                  statuses?.content === 'APPROVED'
                }
              />
            </Box>
          </Grid2>

          {/* ستون دوم */}
          <Grid2 component="div" xs={12} md={4} lg={4}>
            <Box sx={cardSx}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'stretch',
                  padding: `${SPACING.lg}px ${SPACING.md}px`,
                  gap: `${SPACING.sm}px`,
                }}
              >
                <EditAppButton
                  text="ویرایش اطلاعات"
                  startIcon={
                    <img
                      src={editIcon}
                      alt="icon"
                      style={{ width: 20, height: 20 }}
                    />
                  }
                  onClick={handleEdit}
                />
              </Box>
              <Box>
                <AppDetailsLeft
                  title="تصاویر اپلیکیشن"
                  images={liveScreenshots}
                  description={description_fa}
                  additionalInfo={description_en}
                />
              </Box>
            </Box>
          </Grid2>

          {/* ستون سوم */}
          <Grid2 component="div" xs={12} md={4} lg={4}>
            <Box sx={cardSx}>
              <ApplicationReport applicationId={appId} />
            </Box>
          </Grid2>
        </Grid2>

        {/* ردیف دوم */}
        <Grid2
          container
          spacing={SPACING.md}
          sx={{ width: '100%', margin: 0 }}
        >
          <Grid2 component="div" xs={12}>
            <AppMediaPromotion
              publisherId={publisherId}
              applicationId={appId}
            />
          </Grid2>
        </Grid2>
      </ContentWrapper>
    </PageContainer>
  );
};

export default Application;
